import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { Spinner } from '@/components/ui/spinner';
import { getAuthSession } from '@/utils/auth-session';
import { ApplicationReturnLink } from '@/components/ApplicationReturnLink';
import { 
  KeyRound, 
  ShieldCheck,
  Fingerprint,
  CheckCircle,
  XCircle,
  ArrowLeft,
  AlertCircle
} from 'lucide-react';

export default function PasskeyVerify() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [credentialId, setCredentialId] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'authenticating' | 'success' | 'error'>('idle');
  const [showDebug, setShowDebug] = useState(false);
  const [debugInfo, setDebugInfo] = useState<string[]>([]);
  const [applicationName, setApplicationName] = useState<string | null>(null);

  // Helper to add debug messages
  const addDebug = (message: string) => {
    console.log('[PasskeyVerify]', message);
    if (showDebug) {
      setDebugInfo(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
    }
  };

  // Get credential ID from localStorage user object
  const getCredentialIdFromStorage = (): string | null => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        if (user.credentials && user.credentials.length > 0) {
          // Return the first credential ID (using consistent 'credentialId' field)
          return user.credentials[0].credentialId;
        }
      }
    } catch (error) {
      console.error('Failed to parse user from localStorage:', error);
    }
    return null;
  };

  const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
    const binaryString = window.atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  };

  const base64urlToArrayBuffer = (base64url: string): ArrayBuffer => {
    // Convert base64url to base64
    const base64 = base64url
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(base64url.length + (4 - base64url.length % 4) % 4, '=');
    
    return base64ToArrayBuffer(base64);
  };

  const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  const authenticateWithPasskey = async () => {
    setLoading(true);
    setStatus('authenticating');
    setDebugInfo([]);

    try {
      // Check if we have a valid session
      const authSession = getAuthSession();
      if (!authSession?.sessionId) {
        throw new Error('No active authentication session. Please start from the login page.');
      }
      
      addDebug(`Starting authentication with credential ID: ${credentialId ? credentialId.substring(0, 20) + '...' : 'NONE'}`);
      
      // Step 1: Get authentication options from server
      const optionsResponse = await fetch('https://identity.tools.agentic.inv.tech/passkey/authenticate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          credentialId: credentialId
        })
      });

      const responseData = await optionsResponse.json();
      
      if (!responseData.success) {
        throw new Error(responseData.error || 'Failed to get authentication options');
      }

      const options = responseData;
      addDebug(`Received options with challenge`);

      // Show prompt to user
      toast({
        title: "Verify your identity",
        description: "Touch your security key when prompted by your browser",
      });

      // Step 2: Use WebAuthn API to get credential
      const credential = await navigator.credentials.get({
        publicKey: {
          challenge: base64ToArrayBuffer(options.publicKey.challenge),
          rpId: options.publicKey.rpId,
          allowCredentials: options.publicKey.allowCredentials?.map((cred: { id: string; type: string; transports?: string[] }) => {
            const isBase64url = cred.id.includes('-') || cred.id.includes('_') || !cred.id.includes('=');
            return {
              ...cred,
              id: isBase64url ? base64urlToArrayBuffer(cred.id) : base64ToArrayBuffer(cred.id)
            };
          }),
          userVerification: options.publicKey.userVerification,
          timeout: options.publicKey.timeout
        }
      }) as PublicKeyCredential;

      const response = credential.response as AuthenticatorAssertionResponse;

      // Step 3: Send credential to server for verification
      const verifyPayload = {
        credentialId: credential.id,
        clientDataJSON: arrayBufferToBase64(response.clientDataJSON),
        authenticatorData: arrayBufferToBase64(response.authenticatorData),
        signature: arrayBufferToBase64(response.signature),
        userHandle: response.userHandle ? arrayBufferToBase64(response.userHandle) : null,
        session_id: authSession?.sessionId // Include session_id if available
      };
      
      const verifyResponse = await fetch('https://identity.tools.agentic.inv.tech/auth/passkey', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(verifyPayload)
      });

      // Handle JSON response
      const result = await verifyResponse.json();

      if (!result.success) {
        throw new Error(result.error || 'Authentication failed');
      }

      // If we got a redirect URL, navigate to it
      if (result.redirectUrl) {
        setStatus('success');
        toast({
          title: "Success!",
          description: "Authentication complete. Redirecting...",
        });
        
        // Clear stored application info before redirect
        sessionStorage.removeItem('application_name');
        sessionStorage.removeItem('application_url');
        
        // Navigate to the redirect URL
        window.location.href = result.redirectUrl;
        return;
      }

      // Should not reach here as successful auth should always return a redirect URL
      throw new Error('Unexpected response from server');

    } catch (err: unknown) {
      console.error('Authentication error:', err);
      setStatus('error');

      const errorTitle = 'Authentication failed';
      let errorDescription = 'Please try again';
      
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          errorDescription = 'Operation cancelled or security key not recognized';
        } else if (err.name === 'InvalidStateError') {
          errorDescription = 'The authenticator was unable to process the request';
        } else if (err.name === 'NotSupportedError') {
          errorDescription = 'This authenticator is not supported';
        } else {
          errorDescription = err.message;
        }
      }

      toast({
        title: errorTitle,
        description: errorDescription,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const credentialId = getCredentialIdFromStorage();
    if (credentialId) {
      setCredentialId(credentialId);
    } else {
      setStatus('error');
      toast({
        title: "No passkey found",
        description: "Please set up a passkey first",
        variant: "destructive",
      });
    }
    
    // Load application name from sessionStorage
    const storedAppName = sessionStorage.getItem('application_name');
    if (storedAppName) {
      setApplicationName(storedAppName);
    }
  }, [toast]);

  useEffect(() => {
    // Auto-start authentication when credential ID is available
    if (credentialId && status === 'idle') {
      authenticateWithPasskey();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentialId]);

  const getStatusIcon = () => {
    switch (status) {
      case 'authenticating':
        return <Spinner size="lg" className="text-blue-600" />;
      case 'success':
        return <CheckCircle className="h-12 w-12 text-green-600 animate-in zoom-in-50 duration-300" />;
      case 'error':
        return <XCircle className="h-12 w-12 text-red-600 animate-in zoom-in-50 duration-300" />;
      default:
        return <ShieldCheck className="h-12 w-12 text-slate-600" />;
    }
  };

  const getStatusMessage = () => {
    switch (status) {
      case 'authenticating':
        return {
          title: 'Verifying your identity',
          description: 'Please touch your security key when prompted'
        };
      case 'success':
        return {
          title: 'Authentication successful',
          description: 'Redirecting you to the application...'
        };
      case 'error':
        return {
          title: 'Authentication failed',
          description: credentialId ? 'Please try again with your security key' : 'No passkey found'
        };
      default:
        return {
          title: 'Ready to authenticate',
          description: 'Click below to verify with your security key'
        };
    }
  };

  const statusMessage = getStatusMessage();

  return (
    <AuthLayout applicationName={applicationName || undefined}>
      <Card className="w-full max-w-md animate-in fade-in-50 duration-500">
        <CardHeader className="text-center pb-3">
          <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 rounded-full flex items-center justify-center animate-in zoom-in-50 duration-300">
            {getStatusIcon()}
          </div>
          <CardTitle className="text-2xl font-semibold tracking-tight">
            {statusMessage.title}
          </CardTitle>
          <CardDescription className="text-sm text-muted-foreground mt-2">
            {statusMessage.description}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {status === 'authenticating' && (
            <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
              <Fingerprint className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertTitle className="text-blue-900 dark:text-blue-100">
                Action required
              </AlertTitle>
              <AlertDescription className="text-blue-800 dark:text-blue-200">
                <ol className="mt-2 ml-2 list-decimal space-y-1 text-sm">
                  <li>Make sure your security key is connected</li>
                  <li>Touch the key when your browser prompts you</li>
                  <li>Wait for verification to complete</li>
                </ol>
              </AlertDescription>
            </Alert>
          )}

          {status === 'error' && credentialId && (
            <div className="space-y-3">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Authentication failed</AlertTitle>
                <AlertDescription>
                  Make sure your security key is properly connected and try again.
                </AlertDescription>
              </Alert>

              <Button 
                onClick={authenticateWithPasskey} 
                className="w-full"
                size="lg"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Authenticating...
                  </>
                ) : (
                  <>
                    <KeyRound className="mr-2 h-5 w-5" />
                    Try again
                  </>
                )}
              </Button>
            </div>
          )}

          {status === 'error' && !credentialId && (
            <div className="space-y-3">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>No passkey found</AlertTitle>
                <AlertDescription>
                  You need to set up a passkey before you can authenticate. Please sign in with Google first.
                </AlertDescription>
              </Alert>
            </div>
          )}

          <Button 
            onClick={() => navigate('/')} 
            className="w-full" 
            variant="outline"
            size="lg"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to login
          </Button>

          {/* Debug toggle */}
          {import.meta.env.DEV && (
            <div className="pt-4 border-t">
              <button
                onClick={() => setShowDebug(!showDebug)}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                {showDebug ? 'Hide' : 'Show'} debug info
              </button>
              
              {showDebug && debugInfo.length > 0 && (
                <div className="mt-2 p-3 bg-muted rounded-lg">
                  <div className="text-xs font-mono space-y-1">
                    {debugInfo.map((info, index) => (
                      <div key={index}>{info}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Application return link */}
      <ApplicationReturnLink />
    </AuthLayout>
  );
}